import type { MouseEvent } from 'react';
import { Link } from 'wouter';
import {
  Flower2, PartyPopper, Gift, Cake,
  Heart, Star, GraduationCap, Users, Home,
  Sparkles, Leaf, Sun, Baby, Crown,
  Music, Snowflake, Church, User,
} from 'lucide-react';
import { collectionUrl } from '@/lib/collectionUrl';

/* ── Tokens ─────────────────────────────────────────────── */
const BG      = '#ffffff';
const LEFT_BG = '#ffffff';
const WHITE   = '#ffffff';
const DARK    = '#2A1F14';
const GOLD    = '#e05590';
const ROSE    = '#9B2335';
const MUTED   = 'rgba(42,31,20,0.58)';
const ICON_BG = '#FAE8E8';

type Icon = React.ComponentType<{ size?: number; strokeWidth?: number; color?: string }>;
interface OkazjaItem   { Icon: Icon; title: string; desc: string }
interface OkazjaContent {
  leftEyebrow: string;
  heading: string;
  desc: string;
  rightEyebrow: string;
  items: OkazjaItem[];
}

/* ── Per-occasion content ───────────────────────────────── */
const OKAZJA_CONTENT: Record<string, OkazjaContent> = {

  /* ─── Urodziny ──────────────────────────────────────── */
  urodziny: {
    leftEyebrow: 'DLACZEGO WARTO WYBRAĆ KWIATY NA URODZINY?',
    heading: 'Piękny gest, który zostaje w pamięci',
    desc: 'Kwiaty potrafią wyrazić więcej niż słowa. Niezależnie od wieku i okazji, bukiet urodzinowy zawsze wywołuje radość i tworzy wyjątkowe wspomnienia. Pomożemy Ci wybrać kompozycję idealnie dopasowaną do osoby, której chcesz sprawić przyjemność.',
    rightEyebrow: 'NAJPOPULARNIEJSZE OKAZJE URODZINOWE',
    items: [
      { Icon: PartyPopper, title: 'Urodziny dla niej',  desc: 'Delikatne i romantyczne kompozycje pełne kolorów i emocji.' },
      { Icon: Gift,        title: 'Urodziny dla niego', desc: 'Stylowe bukiety w męskim wydaniu – eleganckie i wyjątkowe.' },
      { Icon: Flower2,     title: '18. urodziny',       desc: 'Wyjątkowy moment, wyjątkowy bukiet na nowy rozdział.' },
      { Icon: Cake,        title: 'Okrągłe urodziny',   desc: '50, 60, 70... Świętuj ważne chwile z pięknymi kwiatami.' },
    ],
  },

  /* ─── Imieniny ─────────────────────────────────────── */
  imieniny: {
    leftEyebrow: 'DLACZEGO WARTO WYBRAĆ KWIATY NA IMIENINY?',
    heading: 'Kwiaty, które mówią: pamiętam o Tobie',
    desc: 'Imieniny to wyjątkowy dzień, który zasługuje na piękny gest. Bukiet kwiatów to najprostszy sposób, by pokazać, że pamiętasz i zależy Ci na bliskiej osobie. Pomożemy wybrać kompozycję idealną dla każdego solenizanta.',
    rightEyebrow: 'POPULARNE KWIATY NA IMIENINY',
    items: [
      { Icon: Flower2,     title: 'Bukiet klasyczny',     desc: 'Ponadczasowa kompozycja, która zawsze zachwyca.' },
      { Icon: Gift,        title: 'Flower box',           desc: 'Elegancki prezent w pięknym pudełku - gotowy do wręczenia.' },
      { Icon: Home,        title: 'Kwiaty w koszu',       desc: 'Wyjątkowa kompozycja do postawienia w domu.' },
      { Icon: Leaf,        title: 'Kwiaty doniczkowe',    desc: 'Prezent, który cieszy przez długi czas.' },
    ],
  },

  /* ─── Rocznica ─────────────────────────────────────── */
  rocznica: {
    leftEyebrow: 'DLACZEGO KWIATY SĄ IDEALNYM PREZENTEM NA ROCZNICĘ?',
    heading: 'Miłość wyrażona kwiatami',
    desc: 'Rocznica to czas, by świętować wspólne chwile i wyrazić to, co w sercu. Romantyczny bukiet róż lub wyjątkowa kompozycja kwiatowa to gest, który powie więcej niż słowa i sprawi, że celebracja stanie się niezapomniana.',
    rightEyebrow: 'NAJPOPULARNIEJSZE KWIATY NA ROCZNICĘ',
    items: [
      { Icon: Heart,   title: 'Czerwone róże',     desc: 'Klasyczny symbol miłości - zawsze robi wrażenie.' },
      { Icon: Flower2, title: 'Pastelowe bukiety', desc: 'Delikatne i romantyczne kompozycje pełne subtelności.' },
      { Icon: Sparkles,title: 'Luksusowe flower boxy', desc: 'Elegancja zamknięta w pięknym pudełku.' },
      { Icon: Gift,    title: 'Personalizowane',   desc: 'Kompozycja stworzona dokładnie z myślą o Waszej rocznicy.' },
    ],
  },

  /* ─── Przeprosiny ──────────────────────────────────── */
  przeprosiny: {
    leftEyebrow: 'DLACZEGO KWIATY POMAGAJĄ W PRZEPROSINACH?',
    heading: 'Słowa, które zastępuje piękno kwiatów',
    desc: 'Przeprosiny wymagają odwagi i szczerego gestu. Bukiet kwiatów mówi to, co czasem trudno wyrazić słowami - pokazuje, że zależy Ci na relacji i zrozumiałeś, że coś poszło nie tak.',
    rightEyebrow: 'KWIATY IDEALNE NA PRZEPROSINY',
    items: [
      { Icon: Flower2, title: 'Białe kwiaty',      desc: 'Symbol szczerości i czystych intencji.' },
      { Icon: Heart,   title: 'Różowe kompozycje', desc: 'Delikatny gest, który łagodzi każdą atmosferę.' },
      { Icon: Gift,    title: 'Czerwone róże',     desc: 'Klasyczny znak miłości i skruchy zarazem.' },
      { Icon: Sparkles,title: 'Mieszane bukiety',  desc: 'Kolorowa kompozycja pełna pozytywnych emocji.' },
    ],
  },

  /* ─── Gratulacje ───────────────────────────────────── */
  gratulacje: {
    leftEyebrow: 'DLACZEGO KWIATY SĄ IDEALNYM GESTEM NA GRATULACJE?',
    heading: 'Świętuj sukcesy z pięknym bukietem',
    desc: 'Gratulacje zasługują na wyjątkową oprawę. Świeży bukiet kwiatów to gest, który podkreśla wagę osiągnięcia i sprawia, że celebracja staje się jeszcze bardziej wyjątkowa i zapamiętana.',
    rightEyebrow: 'POPULARNE OKAZJE DO GRATULACJI',
    items: [
      { Icon: Star,          title: 'Awans zawodowy',  desc: 'Doceniaj sukcesy zawodowe pięknym bukietem.' },
      { Icon: GraduationCap, title: 'Obrona pracy',    desc: 'Uwieńcz ciężką pracę elegancką kompozycją.' },
      { Icon: Sparkles,      title: 'Egzamin zdany',   desc: 'Uczcij ten sukces pięknymi, świeżymi kwiatami.' },
      { Icon: Gift,          title: 'Nowa praca',      desc: 'Zacznij nowy rozdział z bukietem na dobry start.' },
    ],
  },

  /* ─── Podziękowania ────────────────────────────────── */
  podziekowania: {
    leftEyebrow: 'DLACZEGO KWIATY SĄ NAJLEPSZYM PODZIĘKOWANIEM?',
    heading: 'Podziękuj z klasą - bukiet, który zostaje w pamięci',
    desc: 'Kwiaty to ponadczasowy sposób na wyrażenie wdzięczności. Niezależnie od okazji, bukiet przekazany z serca zawsze robi wrażenie i jest przyjmowany z radością i wzruszeniem.',
    rightEyebrow: 'POPULARNE BUKIETY NA PODZIĘKOWANIA',
    items: [
      { Icon: User,    title: 'Dla nauczyciela',     desc: 'Piękne podziękowanie za wiedzę i cierpliwość.' },
      { Icon: Heart,   title: 'Dla lekarza',         desc: 'Wyraź wdzięczność za opiekę i troskę.' },
      { Icon: Users,   title: 'Dla współpracownika', desc: 'Gest uznania dla kogoś, kto zawsze pomaga.' },
      { Icon: Flower2, title: 'Dla przyjaciela',     desc: 'Podziękuj za przyjaźń i wsparcie.' },
    ],
  },

  /* ─── Niespodzianka ────────────────────────────────── */
  niespodzianka: {
    leftEyebrow: 'DLACZEGO KWIATY TO IDEALNA NIESPODZIANKA?',
    heading: 'Zaskocz kogoś bliskiego pięknym bukietem',
    desc: 'Niespodzianka nie musi czekać na okazję. Bukiet kwiatów bez powodu to jeden z najpiękniejszych gestów - pokazuje, że myślisz o kimś bliskim i chcesz sprawić mu radość.',
    rightEyebrow: 'KWIATY JAKO NIESPODZIANKA',
    items: [
      { Icon: Heart,       title: 'Bez okazji',       desc: 'Najpiękniejszy bukiet to ten bez powodu.' },
      { Icon: Sun,         title: 'Na dzień dobry',   desc: 'Rozjaśnij komuś dzień niespodziewanym kwiatem.' },
      { Icon: Home,        title: 'Do domu',          desc: 'Piękna dekoracja, która ucieszy na co dzień.' },
      { Icon: Sparkles,    title: 'Z dostawą',        desc: 'Wyślij niespodziankę prosto pod drzwi.' },
    ],
  },

  /* ─── Komunia ──────────────────────────────────────── */
  komunia: {
    leftEyebrow: 'DLACZEGO KWIATY SĄ PIĘKNYM GESTEM NA KOMUNIĘ?',
    heading: 'Wyjątkowy dzień, wyjątkowe kwiaty',
    desc: 'Pierwsza Komunia Święta to jeden z najważniejszych momentów w życiu dziecka i całej rodziny. Delikatna kompozycja kwiatowa w bieli pięknie uzupełni ten wyjątkowy, uroczysty dzień.',
    rightEyebrow: 'POPULARNE KWIATY NA KOMUNIĘ',
    items: [
      { Icon: Church,  title: 'Białe kompozycje',  desc: 'Czyste i eleganckie - idealne na komunię.' },
      { Icon: Flower2, title: 'Delikatne pastele', desc: 'Subtelne barwy podkreślające wyjątkowość dnia.' },
      { Icon: Gift,    title: 'Flower boxy',       desc: 'Elegancki prezent w pięknym opakowaniu.' },
      { Icon: Home,    title: 'Kwiaty w koszu',    desc: 'Wyjątkowa kompozycja do domu na pamiątkę.' },
    ],
  },

  /* ─── Obrona ───────────────────────────────────────── */
  obrona: {
    leftEyebrow: 'DLACZEGO KWIATY NA OBRONĘ PRACY TO ŚWIETNY POMYSŁ?',
    heading: 'Doceniaj sukcesy - gratuluj z kwiatami',
    desc: 'Obrona pracy dyplomowej to uwieńczenie lat ciężkiej nauki i pracy. Bukiet kwiatów to jeden z najbardziej osobistych i pięknych sposobów, by pogratulować tego wyjątkowego sukcesu.',
    rightEyebrow: 'POPULARNE BUKIETY NA OBRONĘ',
    items: [
      { Icon: GraduationCap, title: 'Klasyczne bukiety',   desc: 'Elegancja i prostota - idealne na ten dzień.' },
      { Icon: Gift,          title: 'Flower boxy',         desc: 'Prezent, który zachwyci każdego absolwenta.' },
      { Icon: Home,          title: 'Kosze kwiatowe',      desc: 'Imponująca kompozycja na wyjątkową chwilę.' },
      { Icon: Star,          title: 'Luksusowe kompozycje',desc: 'Dla kogoś, kto osiągnął coś naprawdę wielkiego.' },
    ],
  },

  /* ─── Zaręczyny ────────────────────────────────────── */
  zareczyny: {
    leftEyebrow: 'DLACZEGO KWIATY SĄ IDEALNE NA ZARĘCZYNY?',
    heading: 'Miłość w rozkwicie - kwiaty na zaręczyny',
    desc: 'Zaręczyny to jeden z najpiękniejszych momentów w życiu. Elegancki bukiet róż lub romantyczna kompozycja kwiatowa doskonale dopełni ten wyjątkowy wieczór i sprawi, że stanie się niezapomnianym wspomnieniem.',
    rightEyebrow: 'POPULARNE KWIATY NA ZARĘCZYNY',
    items: [
      { Icon: Heart,       title: 'Czerwone róże',       desc: 'Klasyczny symbol miłości - zawsze robi wrażenie.' },
      { Icon: Sparkles,    title: 'Romantyczne bukiety', desc: 'Delikatne i eleganckie kompozycje pełne emocji.' },
      { Icon: Crown,       title: 'Luksusowe flower boxy',desc: 'Wyjątkowy gest na wyjątkową chwilę.' },
      { Icon: Flower2,     title: 'Piwonie i róże',      desc: 'Romantyczna mieszanka pięknych, bujnych kwiatów.' },
    ],
  },

  /* ─── Dla pary młodej ──────────────────────────────── */
  'dla-pary-mlodej': {
    leftEyebrow: 'DLACZEGO KWIATY SĄ IDEALNYM PREZENTEM DLA PARY MŁODEJ?',
    heading: 'Piękno kwiatów na początku wspólnej drogi',
    desc: 'Ślub to wyjątkowy dzień, który zasługuje na piękną kwiatową oprawę. Prezent w postaci eleganckiej kompozycji kwiatowej to gest, który Para Młoda zapamięta i będzie wspominać przez długie lata.',
    rightEyebrow: 'POPULARNE KWIATY DLA PARY MŁODEJ',
    items: [
      { Icon: Heart,   title: 'Ślubne bukiety',       desc: 'Eleganckie kompozycje na ten wyjątkowy dzień.' },
      { Icon: Flower2, title: 'Dekoracje kwiatowe',   desc: 'Piękna oprawa dla całej uroczystości.' },
      { Icon: Gift,    title: 'Flower boxy',          desc: 'Luksusowy prezent w eleganckiej formie.' },
      { Icon: Sparkles,title: 'Romantyczne',          desc: 'Pełne emocji kompozycje na miłosne chwile.' },
    ],
  },

  /* ─── Dla mamy ─────────────────────────────────────── */
  'dla-mamy': {
    leftEyebrow: 'DLACZEGO KWIATY SĄ IDEALNYM PREZENTEM DLA MAMY?',
    heading: 'Dla najważniejszej osoby na świecie',
    desc: 'Mama zasługuje na coś wyjątkowego. Bukiet świeżych kwiatów to gest, który wyraża miłość i wdzięczność - piękny, osobisty i zawsze przyjmowany z uśmiechem i wzruszeniem.',
    rightEyebrow: 'POPULARNE KWIATY DLA MAMY',
    items: [
      { Icon: Heart,   title: 'Ulubione kwiaty',   desc: 'Dobieramy według gustu i preferencji mamy.' },
      { Icon: Flower2, title: 'Piwonie i róże',    desc: 'Romantyczna mieszanka bujnych, pięknych kwiatów.' },
      { Icon: Sparkles,title: 'Pastelowe bukiety',desc: 'Delikatne barwy, które zachwycają subtelnością.' },
      { Icon: Gift,    title: 'Flower boxy',       desc: 'Elegancki prezent gotowy do wręczenia.' },
    ],
  },

  /* ─── Dla żony ─────────────────────────────────────── */
  'dla-zony': {
    leftEyebrow: 'DLACZEGO KWIATY SĄ IDEALNYM PREZENTEM DLA ŻONY?',
    heading: 'Kwiaty, które mówią: kocham Cię',
    desc: 'Żona zasługuje na piękny gest - nie tylko od święta. Bukiet świeżych kwiatów to sposób, by wyrazić miłość, wdzięczność i troskę w najprostszy, a jednocześnie wyjątkowy sposób.',
    rightEyebrow: 'POPULARNE KWIATY DLA ŻONY',
    items: [
      { Icon: Heart,   title: 'Czerwone róże',      desc: 'Klasyczny symbol miłości na każdą okazję.' },
      { Icon: Flower2, title: 'Romantyczne bukiety',desc: 'Delikatne i piękne kompozycje pełne emocji.' },
      { Icon: Sparkles,title: 'Piwonie',            desc: 'Bujne, aromatyczne kwiaty, które zachwycają.' },
      { Icon: Gift,    title: 'Flower boxy',        desc: 'Elegancki prezent w pięknym opakowaniu.' },
    ],
  },

  /* ─── Dla dziewczyny ───────────────────────────────── */
  'dla-dziewczyny': {
    leftEyebrow: 'DLACZEGO KWIATY SĄ IDEALNYM PREZENTEM DLA DZIEWCZYNY?',
    heading: 'Zaskocz ją - bukiet, który zostaje w pamięci',
    desc: 'Bukiet kwiatów to jeden z najpiękniejszych gestów, jakie możesz zrobić dla swojej dziewczyny. Delikatne, kolorowe lub klasyczne - pomożemy wybrać coś, co ją zachwyci i wywoła szczery uśmiech.',
    rightEyebrow: 'POPULARNE KWIATY DLA DZIEWCZYNY',
    items: [
      { Icon: Flower2, title: 'Pastelowe bukiety', desc: 'Delikatne barwy dla osoby, która lubi spokojne kompozycje.' },
      { Icon: Sparkles,title: 'Kolorowe',           desc: 'Radosne, pełne energii kompozycje.' },
      { Icon: Heart,   title: 'Romantyczne',        desc: 'Czerwone lub różowe - serce na tacy.' },
      { Icon: Gift,    title: 'Flower boxy',        desc: 'Elegancki prezent w pięknym pudełku.' },
    ],
  },

  /* ─── Dla babci ────────────────────────────────────── */
  'dla-babci': {
    leftEyebrow: 'DLACZEGO KWIATY SĄ IDEALNYM PREZENTEM DLA BABCI?',
    heading: 'Dla wyjątkowej babci - kwiaty prosto z serca',
    desc: 'Babcia zasługuje na coś pięknego. Bukiet świeżych kwiatów to gest, który zawsze wywołuje uśmiech i jest najlepszym sposobem, by pokazać, jak bardzo ją kochasz i cenisz.',
    rightEyebrow: 'POPULARNE KWIATY DLA BABCI',
    items: [
      { Icon: Flower2, title: 'Klasyczne bukiety',    desc: 'Ponadczasowe kompozycje, które zawsze cieszą.' },
      { Icon: Heart,   title: 'Goździki',             desc: 'Aromatyczne i trwałe - zawsze w cenie u babci.' },
      { Icon: Sparkles,title: 'Pastelowe bukiety',   desc: 'Delikatne barwy pełne ciepła i serdeczności.' },
      { Icon: Leaf,    title: 'Kwiaty doniczkowe',    desc: 'Prezent, który cieszy przez długi czas.' },
    ],
  },

  /* ─── Dla mężczyzny ────────────────────────────────── */
  'dla-mezczyzny': {
    leftEyebrow: 'DLACZEGO KWIATY SĄ DOBRYM PREZENTEM DLA MĘŻCZYZNY?',
    heading: 'Stylowy gest dla wyjątkowego mężczyzny',
    desc: 'Kwiaty to nie tylko prezent dla kobiet. Elegancki bukiet lub nowoczesna kompozycja to wyjątkowy gest, który zaskoczy i zachwyci każdego mężczyznę - bo piękno docenia każdy.',
    rightEyebrow: 'POPULARNE KWIATY DLA MĘŻCZYZNY',
    items: [
      { Icon: Star,    title: 'Nowoczesne kompozycje', desc: 'Stylowe bukiety w męskim, minimalistycznym wydaniu.' },
      { Icon: Gift,    title: 'Flower boxy',           desc: 'Elegancki prezent w pięknym opakowaniu.' },
      { Icon: Home,    title: 'Kwiaty w koszu',        desc: 'Imponująca kompozycja do postawienia w domu.' },
      { Icon: Leaf,    title: 'Kwiaty doniczkowe',     desc: 'Długotrwały prezent, który ozdabia wnętrze.' },
    ],
  },

  /* ─── Dla przyjaciela ──────────────────────────────── */
  'dla-przyjaciela': {
    leftEyebrow: 'DLACZEGO KWIATY SĄ PIĘKNYM GESTEM DLA PRZYJACIELA?',
    heading: 'Przyjaźń wyrażona kwiatami',
    desc: 'Kwiaty to nie tylko romantyczny gest - to piękny sposób na okazanie przyjaźni i wdzięczności. Przekaż komuś bliskiemu bukiet i pokaż, jak wiele dla Ciebie znaczy ta relacja.',
    rightEyebrow: 'POPULARNE KWIATY DLA PRZYJACIELA',
    items: [
      { Icon: Sun,     title: 'Słoneczniki',       desc: 'Pełne radości - idealne dla kogoś bliskiego.' },
      { Icon: Sparkles,title: 'Kolorowe gerbery',  desc: 'Radosne i energetyczne - jak dobra przyjaźń.' },
      { Icon: Flower2, title: 'Mieszane bukiety',  desc: 'Kolorowa różnorodność, jak każda prawdziwa przyjaźń.' },
      { Icon: Gift,    title: 'Flower boxy',       desc: 'Elegancka niespodzianka gotowa do wręczenia.' },
    ],
  },

  /* ─── Dzień Babci i Dziadka ────────────────────────── */
  'dzien-babci-i-dziadka': {
    leftEyebrow: 'DLACZEGO KWIATY SĄ IDEALNYM GESTEM NA DZIEŃ DZIADKÓW?',
    heading: 'Dla wyjątkowych dziadków - kwiaty z miłością',
    desc: 'Dzień Babci i Dzień Dziadka to okazja, by wyrazić wdzięczność i miłość do najwyjątkniejszych osób w rodzinie. Bukiet świeżych kwiatów to piękny, osobisty gest prosto z serca.',
    rightEyebrow: 'POPULARNE KWIATY DLA DZIADKÓW',
    items: [
      { Icon: Heart,   title: 'Dla babci',           desc: 'Delikatne i aromatyczne bukiety, które zachwycają.' },
      { Icon: Users,   title: 'Dla dziadka',         desc: 'Stylowe i eleganckie kompozycje dla niego.' },
      { Icon: Flower2, title: 'Klasyczne bukiety',   desc: 'Ponadczasowe kompozycje zawsze odpowiednie.' },
      { Icon: Leaf,    title: 'Kwiaty doniczkowe',   desc: 'Długotrwały prezent, który pięknie zdobi dom.' },
    ],
  },

  /* ─── Walentynki ───────────────────────────────────── */
  walentynki: {
    leftEyebrow: 'DLACZEGO KWIATY SĄ IDEALNYM PREZENTEM NA WALENTYNKI?',
    heading: 'Miłość kwitnie w każdym płatku',
    desc: 'Walentynki to dzień, by otwarcie wyrazić swoje uczucia. Klasyczny bukiet czerwonych róż, romantyczna kompozycja lub wyjątkowy flower box - wybierz gest, który trafi prosto do serca ukochanej osoby.',
    rightEyebrow: 'POPULARNE KWIATY NA WALENTYNKI',
    items: [
      { Icon: Heart,   title: 'Czerwone róże',       desc: 'Klasyczny symbol miłości - zawsze trafia w serce.' },
      { Icon: Flower2, title: 'Romantyczne bukiety', desc: 'Delikatne kompozycje pełne miłości i emocji.' },
      { Icon: Sparkles,title: 'Luksusowe flower boxy', desc: 'Wyjątkowy prezent dla wyjątkowej osoby.' },
      { Icon: Crown,   title: 'Piwonie i eustomy',  desc: 'Bujne, eleganckie kwiaty pełne romantyzmu.' },
    ],
  },

  /* ─── Dzień Kobiet ─────────────────────────────────── */
  'dzien-kobiet': {
    leftEyebrow: 'DLACZEGO KWIATY SĄ IDEALNYM GESTEM NA DZIEŃ KOBIET?',
    heading: 'Świętuj każdą kobietę z pięknym bukietem',
    desc: 'Dzień Kobiet to okazja, by docenić wszystkie wyjątkowe kobiety w Twoim życiu. Bukiet świeżych kwiatów to najprostszy i najpiękniejszy sposób na wyrażenie szacunku, podziwu i wdzięczności.',
    rightEyebrow: 'POPULARNE KWIATY NA DZIEŃ KOBIET',
    items: [
      { Icon: Flower2, title: 'Tulipany',         desc: 'Wiosenny klasyk uwielbiany przez kobiety.' },
      { Icon: Heart,   title: 'Róże',             desc: 'Elegancki symbol kobiecości i piękna.' },
      { Icon: Sun,     title: 'Gerbery',          desc: 'Radosne i kolorowe - rozjaśniają każdy dzień.' },
      { Icon: Gift,    title: 'Flower boxy',      desc: 'Elegancki prezent w pięknej formie.' },
    ],
  },

  /* ─── Wielkanoc ────────────────────────────────────── */
  wielkanoc: {
    leftEyebrow: 'DLACZEGO KWIATY PIĘKNIE DEKORUJĄ WIELKANOC?',
    heading: 'Wiosna i radość - kwiaty na Wielkanoc',
    desc: 'Wielkanoc to czas odrodzenia i radości. Wiosenne kwiaty - tulipany, narcyzy, hiacynty - idealnie wpisują się w świąteczny klimat i pięknie ozdabiają wielkanocny stół i dom.',
    rightEyebrow: 'POPULARNE KWIATY NA WIELKANOC',
    items: [
      { Icon: Flower2, title: 'Tulipany',           desc: 'Wiosenne, kolorowe i radosne - idealne na Wielkanoc.' },
      { Icon: Sun,     title: 'Wiosenne kompozycje',desc: 'Pełne świeżości i energii wiosennych kwiatów.' },
      { Icon: Leaf,    title: 'Kwiaty doniczkowe',  desc: 'Piękna dekoracja, która cieszy przez długi czas.' },
      { Icon: Sparkles,title: 'Kosze wielkanocne',  desc: 'Wyjątkowa kompozycja na świąteczny stół.' },
    ],
  },

  /* ─── Dzień Matki ──────────────────────────────────── */
  'dzien-matki': {
    leftEyebrow: 'DLACZEGO KWIATY SĄ NAJPIĘKNIEJSZYM PREZENTEM NA DZIEŃ MATKI?',
    heading: 'Mama zasługuje na najpiękniejsze kwiaty',
    desc: 'Dzień Matki to wyjątkowa okazja, by podziękować za wszystko, co mama dla nas robi. Bukiet świeżych kwiatów to gest miłości, który zawsze wywołuje wzruszenie i sprawia autentyczną radość.',
    rightEyebrow: 'NAJPOPULARNIEJSZE KWIATY NA DZIEŃ MATKI',
    items: [
      { Icon: Heart,   title: 'Piwonie',          desc: 'Bujne i aromatyczne - mama je uwielbia.' },
      { Icon: Flower2, title: 'Tulipany',          desc: 'Wiosenny klasyk, który zawsze cieszy.' },
      { Icon: Sparkles,title: 'Róże',             desc: 'Eleganckie i ponadczasowe - zawsze trafiony wybór.' },
      { Icon: Gift,    title: 'Flower boxy',      desc: 'Elegancki prezent gotowy do wręczenia.' },
    ],
  },

  /* ─── Dzień Dziecka ────────────────────────────────── */
  'dzien-dziecka': {
    leftEyebrow: 'DLACZEGO KWIATY SĄ PIĘKNYM GESTEM NA DZIEŃ DZIECKA?',
    heading: 'Radość i kolory na Dzień Dziecka',
    desc: 'Dzień Dziecka to czas zabawy i uśmiechu. Kolorowe, radosne kwiaty - słoneczniki, gerbery, tulipany - to wyjątkowy prezent, który rozjaśni twarz każdego dziecka i sprawi autentyczną radość.',
    rightEyebrow: 'POPULARNE KWIATY NA DZIEŃ DZIECKA',
    items: [
      { Icon: Sun,     title: 'Słoneczniki',      desc: 'Radosne jak uśmiech dziecka - pełne słońca.' },
      { Icon: Sparkles,title: 'Kolorowe gerbery', desc: 'Żywe barwy, które zachwycają każde dziecko.' },
      { Icon: Baby,    title: 'Kolorowe bukiety', desc: 'Wesołe kompozycje pełne energii i radości.' },
      { Icon: Gift,    title: 'Dla córki',        desc: 'Delikatne i słodkie - idealne dla małej księżniczki.' },
    ],
  },

  /* ─── Dzień Ojca ───────────────────────────────────── */
  'dzien-ojca': {
    leftEyebrow: 'DLACZEGO KWIATY SĄ DOBRYM GESTEM NA DZIEŃ OJCA?',
    heading: 'Tata też zasługuje na kwiaty',
    desc: 'Dzień Ojca to okazja, by podziękować tacie za wszystko, co dla nas robi. Stylowy bukiet lub nowoczesna kompozycja kwiatowa to wyjątkowy gest, który pokaże, jak bardzo go cenisz.',
    rightEyebrow: 'POPULARNE KWIATY NA DZIEŃ OJCA',
    items: [
      { Icon: Star,    title: 'Nowoczesne kompozycje', desc: 'Stylowe bukiety w męskim wydaniu.' },
      { Icon: Gift,    title: 'Flower boxy',           desc: 'Elegancki prezent w pięknym opakowaniu.' },
      { Icon: Home,    title: 'Kwiaty w koszu',        desc: 'Imponująca kompozycja do domu lub biura.' },
      { Icon: Leaf,    title: 'Kwiaty doniczkowe',     desc: 'Długotrwały prezent, który ozdabia wnętrze.' },
    ],
  },

  /* ─── Wszystkich Świętych ──────────────────────────── */
  'wszystkich-swietych': {
    leftEyebrow: 'DLACZEGO KWIATY SĄ WAŻNĄ CZĘŚCIĄ WSZYSTKICH ŚWIĘTYCH?',
    heading: 'Godne upamiętnienie z piękną florystyką',
    desc: 'Wszystkich Świętych to czas zadumy i pamięci o bliskich. Chryzantemy, wrzos, wieńce i stroiki - zadbamy o to, by kwiaty były godną i piękną formą upamiętnienia tych, których wspominamy.',
    rightEyebrow: 'POPULARNE KWIATY NA WSZYSTKICH ŚWIĘTYCH',
    items: [
      { Icon: Church,  title: 'Chryzantemy',      desc: 'Tradycyjny kwiat na cmentarz - dostojny i elegancki.' },
      { Icon: Flower2, title: 'Wieńce',           desc: 'Piękna i godna dekoracja nagrobna.' },
      { Icon: Leaf,    title: 'Wrzos',            desc: 'Symbol pamięci i wieczności.' },
      { Icon: Sparkles,title: 'Stroiki',          desc: 'Kompozycje do umieszczenia na miejscu pamięci.' },
    ],
  },

  /* ─── Boże Narodzenie ──────────────────────────────── */
  'boze-narodzenie': {
    leftEyebrow: 'DLACZEGO KWIATY TO PIĘKNY PREZENT NA BOŻE NARODZENIE?',
    heading: 'Świąteczna magia zamknięta w kwiatach',
    desc: 'Boże Narodzenie to czas wyjątkowy - czas dawania i bycia razem. Świąteczna kompozycja kwiatowa lub gwiazdka betlejemska to piękny prezent i dekoracja, która wprawia w prawdziwy świąteczny nastrój.',
    rightEyebrow: 'POPULARNE KWIATY NA BOŻE NARODZENIE',
    items: [
      { Icon: Star,     title: 'Gwiazdka betlejemska', desc: 'Świąteczna ikona - piękna i trwała dekoracja.' },
      { Icon: Flower2,  title: 'Amarylisy',            desc: 'Okazałe i eleganckie kwiaty na święta.' },
      { Icon: Snowflake,title: 'Zimowe wieńce',        desc: 'Dekoracyjne kompozycje na drzwi i stół.' },
      { Icon: Sparkles, title: 'Świąteczne kompozycje',desc: 'Wyjątkowe aranżacje w świątecznej kolorystyce.' },
    ],
  },
};

/* ── Common pills ────────────────────────────────────────── */
const COMMON_PILLS = [
  { label: 'Urodziny',   href: collectionUrl('urodziny') },
  { label: 'Imieniny',   href: collectionUrl('imieniny') },
  { label: 'Rocznica',   href: collectionUrl('rocznica') },
  { label: 'Walentynki', href: collectionUrl('walentynki') },
  { label: 'Dzień Matki',href: collectionUrl('dzien-matki') },
  { label: 'Wszystkie okazje', href: '/okazje/' },
];

/* ── Component ──────────────────────────────────────────── */
interface Props { handle: string }

export function OkazjaBanner({ handle }: Props) {
  const content = OKAZJA_CONTENT[handle];
  if (!content) return null;

  return (
    <section aria-label={content.leftEyebrow}>

      {/* ── Two-panel strip ──────────────────────────────── */}
      <div className="page-container" style={{
        display: 'flex',
        flexWrap: 'wrap',
        columnGap: 'clamp(32px, 4vw, 56px)',
        borderTop: '1px solid rgba(42,31,20,0.10)',
      }}>

        {/* LEFT — editorial panel */}
        <div style={{
          flex: '1 1 300px',
          background: LEFT_BG,
          paddingBlock: 'clamp(32px, 4vw, 52px)',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 'clamp(14px, 2vw, 20px)',
        }}>
          <p style={{
            margin: 0,
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: GOLD,
          }}>
            {content.leftEyebrow}
          </p>

          <h2 style={{
            margin: 0,
            fontFamily: 'Cormorant Garamond, Georgia, serif',
            fontSize: 'clamp(24px, 3vw, 36px)',
            fontWeight: 400,
            lineHeight: 1.18,
            color: DARK,
            letterSpacing: '-0.01em',
          }}>
            {content.heading}
          </h2>

          <p style={{
            margin: 0,
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 'clamp(13px, 1.35vw, 14.5px)',
            lineHeight: 1.78,
            color: MUTED,
            maxWidth: 420,
          }}>
            {content.desc}
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <Flower2 size={14} color={ROSE} strokeWidth={1.8} />
            <span style={{
              fontFamily: 'DM Sans, sans-serif',
              fontSize: 12.5,
              color: ROSE,
              fontWeight: 500,
              letterSpacing: '0.01em',
            }}>
              Bukietowa - kwiaty z pasją od 2016 roku
            </span>
          </div>
        </div>

        {/* RIGHT — items panel */}
        <div style={{
          flex: '1.5 1 360px',
          background: WHITE,
          paddingBlock: 'clamp(32px, 4vw, 52px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'clamp(20px, 3vw, 32px)',
        }}>
          <p style={{
            margin: 0,
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 9,
            fontWeight: 600,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            color: GOLD,
          }}>
            {content.rightEyebrow}
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: 'clamp(16px, 2.5vw, 28px)',
          }}>
            {content.items.map(({ Icon, title, desc }) => (
              <div key={title} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12 }}>
                <div style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  background: ICON_BG,
                  border: `1px solid rgba(224,85,144,0.22)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Icon size={36} color={ROSE} strokeWidth={1.2} />
                </div>
                <p style={{
                  margin: 0,
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 'clamp(12.5px, 1.3vw, 13.5px)',
                  fontWeight: 600,
                  color: DARK,
                  lineHeight: 1.3,
                }}>
                  {title}
                </p>
                <p style={{
                  margin: 0,
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 'clamp(11.5px, 1.2vw, 12.5px)',
                  lineHeight: 1.6,
                  color: MUTED,
                }}>
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Pill bar ─────────────────────────────────────── */}
      <div className="page-container" style={{
        background: BG,
        borderTop: '1px solid rgba(210,60,120,0.18)',
        paddingBlock: 'clamp(20px, 3.5vw, 32px)',
      }}>
        <p style={{
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 9,
          letterSpacing: '0.38em',
          textTransform: 'uppercase',
          color: GOLD,
          textAlign: 'center',
          margin: '0 0 clamp(12px, 2vw, 16px)',
        }}>
          Najczęściej wybierane okazje
        </p>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 'clamp(8px, 1.2vw, 10px)',
        }}>
          {COMMON_PILLS.map(({ label, href }) => (
            <Link
              key={label}
              href={href}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                padding: '8px 16px',
                borderRadius: 999,
                border: '1px solid rgba(210,60,120,0.28)',
                background: '#ffffff',
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 'clamp(11px, 1.2vw, 13px)',
                color: DARK,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                transition: 'border-color 0.2s, box-shadow 0.2s',
              }}
              onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(210,60,120,0.6)';
                (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 1px 6px rgba(42,31,20,0.08)';
              }}
              onMouseLeave={(e: MouseEvent<HTMLAnchorElement>) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = 'rgba(210,60,120,0.28)';
                (e.currentTarget as HTMLAnchorElement).style.boxShadow = 'none';
              }}
            >
              <Flower2 size={13} color={ROSE} strokeWidth={1.8} />
              {label}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Set of handles that use this banner ─────────────────── */
export const OKAZJA_BANNER_HANDLES = new Set(Object.keys(OKAZJA_CONTENT));
